import React, { useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import Tooltip from 'components/Tooltip';
import { useOrganizationContext } from 'src/contexts/OrganizationContext';

/**
 * OrganizationGuard - Role-based access control wrapper component
 *
 * Conditionally renders children based on user's role in active organization.
 */
const OrganizationGuard = memo(
	({
		children,
		requires = null,
		deny = null,
		fallback = null,
		showTooltip = false,
		tooltipMessage = 'You do not have permission to perform this action',
		tooltipDirection = 'bottom',
		inverse = false,
	}) => {
		const { activeOrganization } = useOrganizationContext();

		// Memoized user role from active organization
		const userRole = useMemo(() => {
			return activeOrganization?.user_role || null;
		}, [activeOrganization]);

		// Memoized access check
		const hasAccess = useMemo(() => {
			// If no organization context, deny access by default
			if (!userRole) {
				return inverse ? true : false;
			}

			let canAccess = false;

			// Check if role is required
			if (requires) {
				const requiredRoles = Array.isArray(requires) ? requires : [requires];
				canAccess = requiredRoles.includes(userRole);
			}
			// Check if role is denied
			else if (deny) {
				const deniedRoles = Array.isArray(deny) ? deny : [deny];
				canAccess = !deniedRoles.includes(userRole);
			}
			// If neither requires nor deny is specified, allow access
			else {
				canAccess = true;
			}

			// Apply inverse logic if specified
			return inverse ? !canAccess : canAccess;
		}, [userRole, requires, deny, inverse]);

		// If no access and no fallback, return null (hide completely)
		if (!hasAccess && !fallback && !showTooltip) {
			return null;
		}

		// If no access but has fallback, show fallback
		if (!hasAccess && fallback) {
			return <>{fallback}</>;
		}

		// If no access but showTooltip is true, wrap children with tooltip
		if (!hasAccess && showTooltip) {
			return (
				<Tooltip text={tooltipMessage} direction={tooltipDirection}>
					<span className="d-inline-block" style={{ cursor: 'not-allowed' }}>
						{children}
					</span>
				</Tooltip>
			);
		}

		// Has access, render children
		return <>{children}</>;
	},
);

OrganizationGuard.displayName = 'OrganizationGuard';

OrganizationGuard.propTypes = {
	/** Content to render if user has access */
	children: PropTypes.node.isRequired,

	/** Required role(s) to view content. Can be string or array of strings */
	requires: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string),
	]),

	/** Denied role(s) that cannot view content. Can be string or array of strings */
	deny: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string),
	]),

	/** Content to show when access is denied */
	fallback: PropTypes.node,

	/** Show tooltip when access is denied */
	showTooltip: PropTypes.bool,

	/** Custom tooltip message */
	tooltipMessage: PropTypes.string,

	/** Tooltip direction */
	tooltipDirection: PropTypes.oneOf(['top', 'bottom', 'left', 'right']),

	/** Inverse the access logic */
	inverse: PropTypes.bool,
};

export default OrganizationGuard;
